import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const TermsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="pt-28 pb-16 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="heading-1 text-foreground mb-4">Terms of Service</h1>
          <p className="text-sm text-muted-foreground mb-12">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>

          <div className="prose max-w-none">
            <section className="mb-10">
              <h2 className="heading-3 text-foreground mb-4">1. Agreement to Terms</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                By accessing or using TMG Impact ("Platform", "Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree, you may not use our services.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                TMG Impact is a CSR intelligence and co-funding platform connecting corporates, NGOs, and government partners for transparent, compliant social impact projects in India.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="heading-3 text-foreground mb-4">2. Eligibility</h2>
              <p className="text-muted-foreground mb-4">To use TMG Impact, you must:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Be at least 18 years old</li>
                <li>Represent a registered entity (company, NGO, trust, society, or government body)</li>
                <li>Provide accurate and complete registration information</li>
                <li>Comply with all applicable laws and regulations, including CSR Act, FCRA, and IT Act</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="heading-3 text-foreground mb-4">3. User Accounts</h2>
              
              <h3 className="heading-4 text-foreground mb-3">3.1 Account Creation</h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>You must create an account to access certain features</li>
                <li>You are responsible for maintaining account confidentiality</li>
                <li>You are liable for all activities under your account</li>
                <li>Notify us immediately of unauthorized access</li>
              </ul>

              <h3 className="heading-4 text-foreground mb-3">3.2 Account Types</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Corporate:</strong> For companies funding CSR projects</li>
                <li><strong>NGO/Implementing Agency:</strong> For organizations executing projects</li>
                <li><strong>Government Partner:</strong> For mission convergence and oversight</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="heading-3 text-foreground mb-4">4. Platform Services</h2>
              
              <h3 className="heading-4 text-foreground mb-3">4.1 For Corporates</h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>Access to vetted CSR projects aligned with Schedule VII</li>
                <li>Direct funding or co-funding options</li>
                <li>Real-time project tracking and impact intelligence</li>
                <li>CSR-2/BRSR compliance reports and auditor-ready exports</li>
              </ul>

              <h3 className="heading-4 text-foreground mb-3">4.2 For NGOs</h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>Project listing and visibility to funders</li>
                <li>Milestone-based funding disbursals</li>
                <li>Evidence submission and verification tools</li>
                <li>Due diligence support and credibility validation</li>
              </ul>

              <h3 className="heading-4 text-foreground mb-3">4.3 For Government</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Real-time CSR activity dashboard by geography and sector</li>
                <li>Mission convergence tools</li>
                <li>Social audit API access</li>
                <li>Evidence vault integration</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="heading-3 text-foreground mb-4">5. User Obligations</h2>
              
              <h3 className="heading-4 text-foreground mb-3">5.1 Prohibited Activities</h3>
              <p className="text-muted-foreground mb-4">You may NOT:</p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>Provide false, misleading, or fraudulent information</li>
                <li>Misrepresent project outcomes or financial data</li>
                <li>Use the platform for illegal activities or money laundering</li>
                <li>Reverse-engineer or scrape platform data</li>
                <li>Interfere with platform security or operations</li>
                <li>Violate intellectual property rights</li>
              </ul>

              <h3 className="heading-4 text-foreground mb-3">5.2 Compliance Requirements</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Corporates must comply with CSR Act, 2013 and amendments</li>
                <li>NGOs must maintain valid 80G/12A registration and file annual returns</li>
                <li>All parties must comply with FCRA, IT Act, and data protection laws</li>
                <li>Evidence submissions must be accurate and verifiable</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="heading-3 text-foreground mb-4">6. Funding and Payments</h2>
              
              <h3 className="heading-4 text-foreground mb-3">6.1 Payment Processing</h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>TMG Impact may facilitate fund transfers between parties</li>
                <li>All transactions are subject to verification and compliance checks</li>
                <li>Platform fees (if applicable) will be clearly disclosed</li>
                <li>Milestone-based releases require evidence verification</li>
              </ul>

              <h3 className="heading-4 text-foreground mb-3">6.2 Refunds and Disputes</h3>
              <p className="text-muted-foreground">
                Disputes regarding fund allocation or project delivery will be resolved through our dispute resolution process. Refunds are subject to verification of breach or non-delivery.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="heading-3 text-foreground mb-4">7. Intellectual Property</h2>
              <p className="text-muted-foreground mb-4">
                TMG Impact and its content (logos, designs, analytics tools, reports) are owned by us or our licensors. You may not copy, modify, or distribute platform content without permission.
              </p>
              <p className="text-muted-foreground">
                By submitting content (project descriptions, photos, reports), you grant us a license to use, display, and share it within the platform ecosystem for service delivery and impact reporting.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="heading-3 text-foreground mb-4">8. Data and Privacy</h2>
              <p className="text-muted-foreground">
                Your use of TMG Impact is subject to our <a href="/privacy" className="font-medium underline" style={{ color: 'hsl(30 10% 25%)' }}>Privacy Policy</a>, which explains how we collect, use, and protect your data. By using the platform, you consent to data processing as described in the Privacy Policy.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="heading-3 text-foreground mb-4">9. Disclaimers</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>TMG Impact is provided "as is" without warranties of any kind</li>
                <li>We do not guarantee project success or impact outcomes</li>
                <li>Due diligence by our advisory team does not eliminate all risks</li>
                <li>You are responsible for your own compliance and legal obligations</li>
                <li>We are not liable for third-party content or actions</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="heading-3 text-foreground mb-4">10. Limitation of Liability</h2>
              <p className="text-muted-foreground">
                To the maximum extent permitted by law, TMG Impact shall not be liable for indirect, incidental, consequential, or punitive damages arising from your use of the platform, including but not limited to lost profits, data loss, or project failures.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="heading-3 text-foreground mb-4">11. Indemnification</h2>
              <p className="text-muted-foreground">
                You agree to indemnify and hold TMG Impact harmless from claims, damages, or expenses arising from your violation of these Terms, misuse of the platform, or breach of applicable laws.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="heading-3 text-foreground mb-4">12. Termination</h2>
              <p className="text-muted-foreground mb-4">
                We may suspend or terminate your account if you:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Violate these Terms</li>
                <li>Engage in fraudulent or illegal activities</li>
                <li>Fail to maintain required registrations or compliance</li>
                <li>Harm the platform or other users</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                You may terminate your account at any time by contacting us. Upon termination, certain data retention obligations may apply.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="heading-3 text-foreground mb-4">13. Governing Law and Dispute Resolution</h2>
              <p className="text-muted-foreground mb-4">
                These Terms are governed by the laws of India. Any disputes shall be resolved through arbitration in Mumbai, India, in accordance with the Arbitration and Conciliation Act, 1996.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="heading-3 text-foreground mb-4">14. Changes to Terms</h2>
              <p className="text-muted-foreground">
                We may modify these Terms at any time. Material changes will be notified via email or platform notification. Continued use after changes constitutes acceptance.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="heading-3 text-foreground mb-4">15. Contact Us</h2>
              <p className="text-muted-foreground mb-4">
                For questions about these Terms, contact:
              </p>
              <div className="card-base p-6">
                <p className="text-foreground font-semibold">TMG Impact</p>
                <p className="text-muted-foreground">Email: legal@tmgimpact.in</p>
                <p className="text-muted-foreground">Address: Mumbai, India</p>
              </div>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t border-border">
            <button 
              onClick={() => navigate('/')}
              className="text-sm font-medium hover:underline"
              style={{ color: 'hsl(30 10% 25%)' }}
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TermsPage;
